/**
 * Privacy Policy page — publicly accessible at /privacy
 * Required for Twilio A2P 10DLC campaign approval (PRIVACY_POLICY_URL)
 */

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-10 border-b border-gray-200 pb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: May 6, 2026</p>
        </div>

        <div className="prose prose-gray max-w-none space-y-8 text-[15px] leading-relaxed">

          {/* Intro */}
          <p>
            APX Advisors LLC ("APX," "we," "us," or "our") is committed to protecting the privacy of our
            clients, prospective clients, and website visitors. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you visit{" "}
            <a href="https://apxsystem.io" className="text-blue-600 hover:underline">apxsystem.io</a> or{" "}
            <a href="https://apx-advisors.com" className="text-blue-600 hover:underline">apx-advisors.com</a>,
            or engage with our bookkeeping, tax, and advisory services, including through third-party
            integrations such as Xero. Please read this policy carefully. If you disagree with its terms,
            please discontinue use of our sites and services.
          </p>

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
            <p className="mb-3">
              We collect information you provide directly to us and information collected automatically
              through your use of our website.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">Information You Provide Directly</h3>
            <p className="mb-3">
              When you complete our contact form, request a consultation, or sign an engagement agreement,
              we may collect the following categories of personal information:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Category</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Examples</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Identifiers</td>
                    <td className="border border-gray-200 px-4 py-2">First name, last name</td>
                    <td className="border border-gray-200 px-4 py-2">Personalize communications</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">Contact Information</td>
                    <td className="border border-gray-200 px-4 py-2">Email address, phone number</td>
                    <td className="border border-gray-200 px-4 py-2">Respond to inquiries, schedule consultations, send SMS notifications</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Business Information</td>
                    <td className="border border-gray-200 px-4 py-2">Business name, industry, annual revenue range</td>
                    <td className="border border-gray-200 px-4 py-2">Assess advisory fit and service level</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">Communication Preferences</td>
                    <td className="border border-gray-200 px-4 py-2">Preferred language (English/Spanish)</td>
                    <td className="border border-gray-200 px-4 py-2">Deliver communications in your preferred language</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Message Content</td>
                    <td className="border border-gray-200 px-4 py-2">Description of financial challenges</td>
                    <td className="border border-gray-200 px-4 py-2">Understand your needs before the first call</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h3 className="font-semibold text-gray-800 mb-2">Information Collected Automatically</h3>
            <p>
              When you visit our website, certain information is collected automatically through standard
              web technologies, including your IP address, browser type and version, operating system,
              referring URLs, pages viewed, and the date and time of your visit. This information is used
              solely for website analytics, security monitoring, and improving user experience. We do not
              use this data to identify individual visitors.
            </p>
          </section>

          {/* 2. SMS / Text Messaging */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. SMS and Text Message Communications</h2>
            <p className="mb-3">
              APX Advisors LLC may send SMS (text message) communications to clients who have provided
              express written consent to receive such messages. This section describes how we collect
              consent, what types of messages we send, and how you can opt out.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">Consent and Opt-In</h3>
            <p className="mb-3">
              We only send SMS messages to clients who have explicitly opted in. Consent is obtained
              through one or more of the following methods:
            </p>
            <ul className="list-disc pl-6 mb-3 space-y-1">
              <li>Signing an engagement agreement that includes a written SMS consent checkbox and disclosure statement.</li>
              <li>Verbally confirming consent during an onboarding call, which is documented in the client file.</li>
              <li>Submitting a contact form that includes an SMS opt-in checkbox.</li>
            </ul>
            <p className="mb-3">
              Consent to receive SMS messages is not a condition of purchasing any service from APX Advisors LLC.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">Types of Messages</h3>
            <p className="mb-3">
              SMS messages sent by APX Advisors LLC may include:
            </p>
            <ul className="list-disc pl-6 mb-3 space-y-1">
              <li>Bookkeeping report completion notifications</li>
              <li>Tax filing appointment reminders</li>
              <li>Document requests (e.g., bank statements, receipts)</li>
              <li>Invoice and payment reminders</li>
              <li>Account status updates</li>
              <li>Responses to client inquiries submitted via text</li>
            </ul>
            <p className="mb-3">
              We do not send marketing or promotional SMS messages. All messages are transactional and
              service-related, sent only to existing clients with active engagements.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">Message Frequency</h3>
            <p className="mb-3">
              Message frequency varies by client and service activity. Most clients receive no more than
              4–8 messages per month. Message and data rates may apply depending on your mobile carrier plan.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">How to Opt Out</h3>
            <p className="mb-3">
              You may opt out of receiving SMS messages from APX Advisors LLC at any time by replying{" "}
              <strong>STOP</strong> to any message we send. After opting out, you will receive a single
              confirmation message and no further SMS communications. To re-subscribe, reply{" "}
              <strong>START</strong> at any time.
            </p>
            <p className="mb-3">
              You may also opt out by contacting us directly at{" "}
              <a href="mailto:info@apx-advisors.com" className="text-blue-600 hover:underline">info@apx-advisors.com</a>{" "}
              or calling <a href="tel:+13104045300" className="text-blue-600 hover:underline">(310) 404-5300</a>.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">Help</h3>
            <p className="mb-3">
              For help with SMS communications, reply <strong>HELP</strong> to any message or contact us at{" "}
              <a href="mailto:info@apx-advisors.com" className="text-blue-600 hover:underline">info@apx-advisors.com</a>.
            </p>
            <h3 className="font-semibold text-gray-800 mb-2">SMS Data Privacy</h3>
            <p>
              We do not share, sell, or disclose your mobile phone number or SMS message content to any
              third party for marketing purposes. Your phone number is used solely to deliver the
              service-related messages described above. SMS message data is retained for no longer than
              24 months and may be deleted upon request.
            </p>
          </section>

          {/* 3. How We Use Your Information */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect for the following business purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>To respond to your inquiry.</strong> When you submit a contact form, we use your name, email, and phone number to contact you within one business day to schedule your free consultation.</li>
              <li><strong>To assess service fit.</strong> Your industry, annual revenue range, and message content help us determine which of our service tiers best fits your business needs.</li>
              <li><strong>To deliver SMS notifications.</strong> With your consent, we use your phone number to send transactional service-related text messages as described in Section 2.</li>
              <li><strong>To communicate in your preferred language.</strong> If you indicate a preference for Spanish, all follow-up communications will be conducted in Spanish.</li>
              <li><strong>To improve our website.</strong> Automatically collected data is used in aggregate to understand how visitors use our site and to improve its content and functionality.</li>
              <li><strong>To comply with legal obligations.</strong> We may use or disclose your information as required by applicable law, regulation, legal process, or governmental request.</li>
            </ul>
          </section>

          {/* 4. Xero Integration */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Collected Through Third-Party Integrations (Xero)</h2>
            <p className="mb-3">
              In the course of providing fractional CFO and financial advisory services, APX uses Xero — a
              cloud-based accounting platform — to access, process, and manage financial data on behalf of
              our clients. When a client authorizes APX to connect to their Xero organization, we may access
              financial records including but not limited to: profit and loss statements, balance sheets, cash
              flow summaries, payroll records, invoices, bills, bank transaction data, and chart of accounts.
              This access is granted solely by the client through Xero's OAuth 2.0 authorization flow and is
              used exclusively to deliver the services described in the client's service agreement with APX.
            </p>
            <p>
              APX acts as a data processor with respect to any financial data accessed through Xero. We do
              not use Xero-sourced data for any purpose other than providing the services the client has
              engaged us to perform. We do not sell, share, or disclose Xero-sourced financial data to any
              third party except as required by law or as explicitly authorized by the client in writing.
              Clients may revoke APX's access to their Xero organization at any time through their Xero
              account settings.
            </p>
          </section>

          {/* 5. How We Share */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. How We Share Your Information</h2>
            <p className="mb-3">
              We do not sell, rent, or trade your personal information to third parties for their marketing
              purposes. We may share your information only in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers.</strong> We use Formspree, Inc. to process and deliver contact form submissions to our team. Formspree acts as a data processor on our behalf and is contractually prohibited from using your data for any purpose other than delivering form submissions.</li>
              <li><strong>SMS Service Provider.</strong> We use Twilio, Inc. to transmit SMS messages to clients who have opted in. Twilio acts as a data processor and is contractually prohibited from using your phone number or message content for any purpose other than message delivery. Twilio's privacy policy is available at <a href="https://www.twilio.com/en-us/legal/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">twilio.com/legal/privacy</a>.</li>
              <li><strong>Xero Limited.</strong> APX integrates with Xero, a cloud accounting platform. When a client authorizes this connection, data flows between APX's internal systems and Xero's platform in accordance with Xero's Developer Platform Terms and Xero's own Privacy Notice.</li>
              <li><strong>Professional Advisors.</strong> We may share information with attorneys, accountants, or other professional advisors who are bound by duties of confidentiality, where necessary to operate our business.</li>
              <li><strong>Legal Requirements.</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
            </ul>
          </section>

          {/* 6. California Privacy Rights */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. California Privacy Rights (CCPA / CPRA)</h2>
            <p className="mb-3">
              If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by
              the California Privacy Rights Act (CPRA), grants you specific rights regarding your personal
              information.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">Your Right</th>
                    <th className="border border-gray-200 px-4 py-2 text-left font-semibold">What It Means</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Right to Know</td>
                    <td className="border border-gray-200 px-4 py-2">You may request disclosure of the categories and specific pieces of personal information we have collected about you, the sources, the business purpose, and the third parties with whom we share it.</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">Right to Delete</td>
                    <td className="border border-gray-200 px-4 py-2">You may request that we delete personal information we have collected from you, subject to certain exceptions.</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Right to Correct</td>
                    <td className="border border-gray-200 px-4 py-2">You may request that we correct inaccurate personal information we maintain about you.</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">Right to Opt-Out of Sale</td>
                    <td className="border border-gray-200 px-4 py-2">We do not sell personal information. No opt-out is necessary.</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2">Right to Non-Discrimination</td>
                    <td className="border border-gray-200 px-4 py-2">We will not discriminate against you for exercising any of your CCPA rights.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:info@apx-advisors.com" className="text-blue-600 hover:underline">info@apx-advisors.com</a>{" "}
              or call <a href="tel:+13104045300" className="text-blue-600 hover:underline">(310) 404-5300</a>.
              We will respond to verifiable consumer requests within 45 days as required by California law.
            </p>
          </section>

          {/* 7. Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Data Retention</h2>
            <p>
              We retain personal information for as long as necessary to fulfill the purpose for which it
              was collected — typically the duration of our business relationship with you plus a reasonable
              period thereafter for record-keeping purposes. Contact form submissions that do not result in
              an engagement are retained for no longer than 24 months. SMS opt-in records are retained for
              the duration of the client relationship and for a minimum of 4 years thereafter to demonstrate
              compliance with applicable regulations. You may request deletion of your information at any
              time as described in Section 6.
            </p>
          </section>

          {/* 8. Security */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Security</h2>
            <p>
              We implement reasonable administrative, technical, and physical safeguards designed to protect
              your personal information from unauthorized access, disclosure, alteration, or destruction.
              All data transmitted through our website and internal systems is encrypted in transit via
              TLS/HTTPS. However, no method of transmission over the internet or method of electronic
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* 9. Children's Privacy */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Children's Privacy</h2>
            <p>
              Our website and services are directed to business owners and are not intended for individuals
              under the age of 18. We do not knowingly collect personal information from children under 18.
              If we become aware that we have inadvertently collected such information, we will delete it
              promptly.
            </p>
          </section>

          {/* 10. Third-Party Links */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy
              practices of those sites and encourage you to review their privacy policies before providing
              any personal information.
            </p>
          </section>

          {/* 11. Changes */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or
              applicable law. When we make material changes, we will update the "Last updated" date at the
              top of this page. We encourage you to review this policy periodically. Your continued use of
              our website after any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          {/* 12. Contact */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
            <p className="mb-2">
              If you have questions or concerns about this Privacy Policy or our privacy practices, or to
              exercise your California privacy rights, please contact us:
            </p>
            <address className="not-italic text-gray-700 space-y-1">
              <div className="font-semibold">APX Advisors LLC</div>
              <div>1225 W 190th St, Los Angeles, CA 90248</div>
              <div>Email: <a href="mailto:info@apx-advisors.com" className="text-blue-600 hover:underline">info@apx-advisors.com</a></div>
              <div>Phone: <a href="tel:+13104045300" className="text-blue-600 hover:underline">(310) 404-5300</a></div>
            </address>
          </section>

        </div>
      </div>
    </div>
  );
}
